import React, { useEffect, useMemo, useState } from 'react';
import { API } from 'aws-amplify';
import { useTranslation } from 'react-i18next';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import { MdRefresh, MdFileDownload } from 'react-icons/md';
import useUIState from '../../hooks/useUIstate';
import { getProductImageUrl } from '../../helpers/commonFunctions';

interface ReplenishmentAdviceProps {
  location: {
    storeId?: number;
    title?: string;
  };
}

type SortKey = 'ProductName' | 'Place' | 'Amount' | 'ActualStock' | 'AmountOverdue' | 'MaxStock';

const ReplenishmentAdvice: React.FC<ReplenishmentAdviceProps> = ({ location }) => {
  const storeId = location?.storeId;
  const { t } = useTranslation();
  const { setShowLoadingModal, setToastMessage, setShowToastMessage, setToastType } = useUIState();
  const [items, setItems]: [any[], any] = useState([]);
  const [products, setProducts]: [any[], any] = useState([]);
  const [customer, setCustomer] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('ProductName');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    const value = localStorage.getItem('UserConfigData');
    if (typeof value === 'string') {
      setCustomer(JSON.parse(value).Customer || '');
    }
  }, []);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, [storeId]);

  function loadData() {
    if (!storeId) return;
    setShowLoadingModal(true);
    Promise.all([
      API.get('AE', `/Stores/GetReplenishmentAdvice?storeId=${storeId}`, { response: true }),
      API.get('AE', '/Products', { response: true }),
    ])
      .then(([advice, prods]: any[]) => {
        setItems(advice.data.Items || []);
        setProducts(prods.data || []);
      })
      .catch(err => console.log(err))
      .finally(() => setShowLoadingModal(false));
  }
  const showDownloadError = () => {
    setToastType("error");
    setToastMessage(t('replenishmentadvice.error.download'));
    setShowToastMessage(true);
  };

  const downloadReplenishmentAdvice = async () => {
    setShowLoadingModal(true);
    API.get("AE", `/Stores/ReplenishmentAdviceLink?storeId=${storeId}`, { response: true })
      .then(async response => {
        const url = response.data.url;
        let parsedUrl: URL;
        try {
          parsedUrl = new URL(url);
        } catch {
          console.error("Export failed: received invalid URL");
          showDownloadError();
          return;
        }
        if (parsedUrl.protocol !== "https:") {
          console.error("Export failed: URL must use HTTPS");
          showDownloadError();
          return;
        }
        const link = document.createElement('a');
        link.href = parsedUrl.href;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch(error => {
        console.error(error);
        showDownloadError();
      })
      .finally(() => setShowLoadingModal(false));
  };
  const rows = useMemo(() => {
    const valueOf = item => (sortKey === 'ProductName' ? item.Product?.ProductName : item[sortKey]);
    return [...items].sort((a, b) => {
      const av = valueOf(a);
      const bv = valueOf(b);
      const cmp = typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av ?? '').localeCompare(String(bv ?? ''));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [items, sortKey, sortDir]);

  function handleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  function imageFor(productId: number) {
    const product = products.find(p => p.Id === productId);
    return product?.Avatar && customer ? getProductImageUrl(product, customer) : null;
  }

  if (!storeId) return <div>No store selected.</div>;

  const columns: { key: SortKey; label: string }[] = [
    { key: 'ProductName', label: t('replenishmentadvice.name') },
    { key: 'Place', label: t('replenishmentadvice.place') },
    { key: 'Amount', label: t('replenishmentadvice.advice') },
    { key: 'ActualStock', label: t('replenishmentadvice.stock') },
    { key: 'AmountOverdue', label: t('replenishmentadvice.inactive-stock') },
    { key: 'MaxStock', label: t('replenishmentadvice.max-stock') },
  ];

  return (
    <div id="dynamicTable">
      <h2>{t('storescreen.replenishment-advice')} — {location?.title}</h2>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell className="add-btn">
              <MdRefresh size={24} style={{ cursor: 'pointer' }} title={t('replenishmentadvice.refresh') as string} onClick={loadData} />
              <MdFileDownload size={24} style={{ cursor: 'pointer', marginLeft: 8 }} title={t('replenishmentadvice.download') as string} onClick={downloadReplenishmentAdvice} />
            </TableCell>
            {columns.map(col => (
              <TableCell key={col.key}>
                <TableSortLabel
                  active={sortKey === col.key}
                  direction={sortKey === col.key ? sortDir : 'asc'}
                  onClick={() => handleSort(col.key)}
                >
                  {col.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(item => (
            <TableRow key={`${item.Place}-${item.Product?.ProductId}`}>
              <TableCell>
                {imageFor(item.Product?.ProductId) ?
                  <img
                    src={imageFor(item.Product?.ProductId) as string}
                    alt=""
                    style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: '50%' }}
                  /> : null}
              </TableCell>
              <TableCell>{item.Product?.ProductName}</TableCell>
              <TableCell>{item.Place}</TableCell>
              <TableCell>{item.Amount}</TableCell>
              <TableCell>{item.ActualStock}</TableCell>
              <TableCell>{item.AmountOverdue}</TableCell>
              <TableCell>{item.MaxStock}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default ReplenishmentAdvice;
